import { createContext, useContext, useEffect, useMemo, useState } from "react";

const EN = {
  nav: {
    home: "Home",
    about: "About us",
    services: "Services",
    contact: "Contact",
    clients: "Clients",
    quote: "Quote on WhatsApp",
    solutions: "NaviCargo solutions",
    chooseService: "Choose the service for your operation",
    new: "New",
    openServices: "Open services submenu",
    closeMenu: "Close menu",
    openMenu: "Open menu",
    clientAccess: "Client access at nnexio.com",
  },
  footer: {
    tagline: "Your cargo moves forward when every decision has backing. We connect suppliers, products and markets to move your business with clarity.",
    cta: "Tell us what you need to move",
    explore: "Explore",
    solutions: "Solutions",
    letsTalk: "Let's talk",
    direct: "Direct support to plan your next logistics operation.",
    quote: "Quote on WhatsApp",
    monitored: "Site monitored by",
    global: "Global logistics · MX",
    rights: "All rights reserved.",
  },
  common: {
    backToServices: "Back to services",
    navicargoLogistics: "NaviCargo Logistics",
    clearInfo: "Clear information",
    directAttention: "Direct support",
    smartDecision: "A smart decision",
    howWeDoIt: "How we do it",
    processTitle: "A process designed to give you certainty",
    nextStep: "Your next step",
    nextText: "You don't need to have everything figured out to get started.",
    nextDescription: "With a few details, we can understand your context, ask the right questions and guide you toward a solution.",
    talkAdvisor: "Talk to an advisor",
    whatsappResponse: "Direct response on WhatsApp",
    alsoInterest: "You may also be interested in",
    viewService: "View service",
    directAttentionLabel: "Direct support",
    contactTeam: "Your cargo, with a team that responds.",
  },
  home: {
    eyebrow: "Clear logistics to operate without borders",
    titleFirst: "Connect your business.",
    titleSecond: "Move the world.",
    description: "NaviCargo is your logistics partner for finding suppliers, inspecting in China, renting warehouses and bringing your cargo through a clear operation from origin to destination.",
    servicesCta: "Explore our services",
    quote: "Quote on WhatsApp",
    viewServices: "View our services",
    viewAll: "View all services",
    viewSolution: "View solution",
    stats: {
      contact: "Point of contact",
      visibility: "Operational visibility",
      tracking: "Operation follow-up",
      global: "Supplier and route network",
    },
    sectionEyebrow: "What we do",
    sectionTitle: "Your operation, in good hands",
    sectionDescription: "We solve the critical points in your supply chain: find, validate, store and move your cargo.",
    viewAll: "View all services",
    unifiedEyebrow: "One partner",
    unifiedTitle: "From sourcing to final destination",
    unifiedDescription: "We connect the pieces that make an international operation possible: we find opportunities, review origin, validate participants and coordinate transportation.",
    aboutCta: "Meet NaviCargo",
    operations: "Borderless operations",
    operationsSub: "Mexico and international markets",
    route: "International route",
    routeTitle: "Import and export with confidence",
    routeText: "We coordinate every stage so you have clarity, control and backing.",
    beforeShipping: "Before shipping",
    beforeTitle: "Decisions with real information",
    beforeCta: "Review my operation",
    connectedMarkets: "Connected markets",
    connectedMarketsText: "We open the way between trusted suppliers and buyers who need to move forward.",
    directAttention: "Direct support",
    shipmentQuestion: "Do you already have a shipment in mind?",
    shipmentText: "Tell us what you need and receive immediate support on WhatsApp.",
    talkNow: "Talk now",
    finalTitle: "Your next operation starts here",
    finalText: "Write to us on WhatsApp with the details of what you need. Our team will guide you quickly and directly.",
    requestQuote: "Request a quote",
    benefits: [
      "One partner to coordinate your operation",
      "Clear information before making decisions",
      "Previously verified suppliers and companies",
      "Close follow-up through delivery",
      "Direct communication on WhatsApp",
      "Solutions adapted to every shipment",
    ],
  },
  services: {
    eyebrow: "Our services",
    titleFirst: "The complete operation,",
    titleSecond: "in one place.",
    description: "From finding the right product to coordinating its arrival: NaviCargo connects every part of your logistics operation with close support and an international view.",
    aboutCta: "Meet NaviCargo",
    quote: "Quote on WhatsApp",
    viewService: "View service",
    advisor: "Talk to an advisor",
    coverage: "How we work",
    coverageTitle: "More clarity at every step",
    coverageDescription: "It is not only about moving cargo. It is about understanding what is at stake and coordinating the right people so your operation can go further.",
    reach: "International reach",
    reachSub: "We connect markets and suppliers",
    verified: "Verified information",
    verifiedSub: "Decide before committing your cargo",
    immediate: "Direct support",
    immediateSub: "Clear communication on WhatsApp",
    whatWeSolve: "What we solve",
    solutionTitle: "Solutions to move forward",
    processTitle: "A clearer way to work",
    steps: [
      { number: "01", title: "We understand your need", description: "Tell us on WhatsApp what product, route or service you need." },
      { number: "02", title: "We build the solution", description: "We analyze options, suppliers, timelines and conditions for your operation." },
      { number: "03", title: "We validate before moving forward", description: "We verify critical information to reduce surprises and risks." },
      { number: "04", title: "We coordinate and follow up", description: "We support the process until your cargo reaches its destination." },
    ],
    freightTitle: "Your cargo needs more than freight",
    freightText: "With NaviCargo you add review, context and coordination. This reduces friction and gives you a partner who responds when you need it most.",
    lessIntermediaries: "Fewer intermediaries",
    moreControl: "More control",
    greaterReach: "Greater reach",
    trustedPartners: "Trusted partners",
    moveQuestion: "What do you need to move?",
    moveText: "Write to us directly and we will design the next step with you.",
  },
  about: {
    eyebrow: "About NaviCargo",
    titleFirst: "Your operation should not feel",
    titleSecond: "complicated.",
    paragraphOne: "NaviCargo Logistics was created to simplify the connection between companies, products and markets. We are the partner that helps you find, verify and move what your business needs.",
    paragraphTwo: "Our work combines logistics coordination, origin review and close communication so you have more control from first contact to delivery.",
    tags: ["Supply chain", "Import / Export", "Inspection"],
    whatsapp: "Talk to us on WhatsApp",
    services: "View services",
    reason: "Our reason for being",
    reasonTitle: "Move opportunities with confidence",
    reasonText: "We believe a good logistics operation starts long before cargo leaves. It starts with information, trusted partners and someone available to coordinate every detail.",
    direction: "Direction and purpose",
    directionTitle: "A logistics company designed to give you certainty.",
    mission: "MISSION",
    missionTitle: "Turn logistics complexity into clear decisions.",
    missionText: "We facilitate trade between companies through sourcing, inspection and coordination so you can move forward with greater confidence and less uncertainty.",
    missionLink: "More clarity before deciding",
    vision: "VISION",
    visionTitle: "Be the partner your operation chooses to move forward.",
    visionText: "We connect markets with human communication, informed decisions and reliable coordination when your cargo needs to move.",
    visionLink: "More control from origin to destination",
    challenge: "Do you have an operation to solve?",
    challengeText: "Tell us the challenge and we will help you find the next step.",
    advisor: "Talk to an advisor",
    valuesEyebrow: "What drives us",
    valuesTitle: "A clearer way to do logistics",
    values: [
      ["Closeness", "Direct support is part of our service. We talk with you, understand your operation and answer without detours."],
      ["Visibility", "We look for the information you need to decide with more context before buying, shipping or partnering."],
      ["Trust", "We reduce risks by verifying suppliers, cargo and critical points in the logistics process."],
      ["Coordination", "We connect the right people so your operation moves forward with one clear line of communication."],
    ],
    commitment: "Our commitment",
    commitmentTitle: "Always on your operation's side",
    commitmentText: "We do not promise logistics will have no challenges. We promise to be present to identify them early, communicate what matters and look for the best way forward.",
    principles: [
      "Personalized support for every operation",
      "Direct and fast communication on WhatsApp",
      "Practical judgment to solve unexpected issues",
      "Transparent commercial relationships",
    ],
    global: "Global",
    globalText: "We connect origins and destinations",
    safe: "Safe",
    safeText: "We validate before moving forward",
    direct: "Direct",
    directText: "Human support on WhatsApp",
    finalTitle: "Let's move your next shipment forward",
    finalText: "Tell us your logistics challenge and receive direct support from our team.",
    finalCta: "Talk to NaviCargo",
  },
  contact: {
    eyebrow: "Contact",
    titleFirst: "Let's talk about your",
    titleSecond: "next operation.",
    description: "You do not need to fill out a form. Write to us directly on WhatsApp and tell us what you need to import, export, verify or transport. Direct support is faster and more personal.",
    quote: "Quote on WhatsApp",
    direct: "Direct support",
    contactInfo: [
      ["WhatsApp", "Get a quote directly from our team", "Open WhatsApp"],
      ["Call us", "", "Call now"],
      ["Write to us", "", "Send email"],
    ],
    attention: "Direct support",
    attentionTitle: "Your cargo, with a team that responds.",
    noIntermediaries: "Support without intermediaries",
    messageTitle: "Your message reaches the right team",
    messageText: "Send us whatever details you already have on WhatsApp: origin, destination, product type, volume, timelines or any question. With that information, we can guide you from the first contact.",
    contextTitle: "Information with context",
    contextText: "We help you identify what matters before moving forward.",
    coordinationTitle: "End-to-end coordination",
    coordinationText: "We connect origin, transportation and destination in one conversation.",
    responseTitle: "Faster response",
    responseText: "WhatsApp is our main channel for handling your quote.",
    ready: "Ready to request a quote?",
    readyText: "Send us a message and let's shape your operation.",
    openWhatsApp: "Open WhatsApp",
    alsoEmail: "You can also write to",
  },
  security: {
    label: "Logistics security",
    verified: "Official channels verified",
    notice: "Security notice · Impersonation and phishing",
    title: "Protect your information and verify our official channels.",
    body: "People outside the company may pretend to be NaviCargo. Always confirm that emails come from @navicargologistics.com and that the phone number or link matches what is published on this site.",
    prevention: "We continuously work to prevent impersonation attempts.",
    warning: "Do not share passwords, codes or banking information.",
    continue: "Understood, continue",
    close: "Close security notice",
  },
  promo: {
    skip: "Skip video",
    close: "Close video",
    enableSound: "Enable sound",
    mute: "Mute",
    unmute: "Enable sound",
    talk: "Talk to an advisor",
    seeVerification: "View company verification",
  },
};

const LanguageContext = createContext(null);

const getValue = (source, path) => path.split(".").reduce((value, key) => value?.[key], source);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem("navicargo-language") === "en" ? "en" : "es";
    } catch {
      return "es";
    }
  });

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      localStorage.setItem("navicargo-language", language);
    } catch {
      // Keep the selected language in memory when storage is unavailable.
    }
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      isEnglish: language === "en",
      t: (key, fallback = "") => (language === "en" ? getValue(EN, key) ?? fallback : fallback),
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider");
  return context;
};